import React from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import UpsellCarousel, { UpsellList } from '~/shared/components/UpsellCarousel';
import {
  useProductDetailSelectionState,
  useProductImages,
  Picture,
  useModelImageContext,
  Separator,
  useBreakpoint,
} from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import useWearItWith from '../../../utils/useWearItWith';
import { CarouselHeading as BaseCarouselHeading } from '../shared';

const CarouselHeading = styled(BaseCarouselHeading)`
  ${({ theme: { pdpStyles } }) => pdpStyles.upsellCarousel.carouselHeading}
`;

const WearItWithSection = styled.section`
  ${({ theme: { pdpStyles } }) => pdpStyles.upsellCarousel.content}
`;

const ProductsWrapper = styled.section`
  ${({ theme: { pdpStyles } }) => pdpStyles.upsellCarousel.productsWrapper}
`;

const ImageWrapper = styled.div`
  ${({ theme: { pdpStyles } }) => pdpStyles.upsellCarousel.imageWrapper}
`;

const wearItWithCarouselCellStyle = css`
  ${({ theme: { pdpStyles } }) => pdpStyles.upsellCarousel.carouselCellStyles}
`;
const StyledPicture = styled(Picture)`
  && {
    width: 167px;
  }

  margin-bottom: 0;
`;

const separatorStyle = css`
  color: ${({ theme }) => theme.colors.separator};
  margin: ${({ theme }) => theme.spacing.sizes.pixels.s}px 0;
`;

const ImageCaption = styled.h3`
  ${({ theme: { pdpStyles } }) => pdpStyles.upsellCarousel.imageCaption}
`;

const MobileCaption = styled.section`
  ${({ theme: { pdpStyles } }) => pdpStyles.upsellCarousel.mobileCaption}
`;

const WearItWithImage = () => {
  const modelImage = useModelImageContext();
  const imageSets = useProductImages({
    images: modelImage?.activeModelImages || {},
  });

  const secondaryImageBreakpoints = [{ minWidth: 0, pictureWidth: 167 }];
  const productImage = imageSets[0];

  if (!productImage) {
    return null;
  }

  return (
    <StyledPicture
      breakpoints={secondaryImageBreakpoints}
      sources={productImage}
    />
  );
};

const RecommendedWearItWith = ({ product }) => {
  const { desktop: isDesktop } = useBreakpoint();
  const productDetailSelection = useProductDetailSelectionState();
  const { isPlusSizeSelected } = productDetailSelection;
  const {
    products,
    productSource,
    shouldDisplay: shouldDisplayWearItWith,
  } = useWearItWith({ product });

  const wearItWithCarouselConfig = {
    groupCellsDesktop: 4,
    minCountPagination: 4,
    autoPlay: false,
    cellStyle: wearItWithCarouselCellStyle,
  };
  if (!shouldDisplayWearItWith) {
    return null;
  }

  return (
    <>
      {isDesktop && (
        <>
          <CarouselHeading data-autotag="pdp-WIW-section-title">
            <FormattedMessage
              id="site_pdp.wear_it_with_heading"
              defaultMessage="Wear It With"
            />
          </CarouselHeading>
          <Separator thickness={2} separatorStyle={separatorStyle} />
        </>
      )}
      <WearItWithSection>
        <ImageWrapper>
          <ImageCaption>
            <FormattedMessage
              id="site_pdp.wear_it_with_image_caption"
              defaultMessage="Current Item"
            />
          </ImageCaption>
          <WearItWithImage />
        </ImageWrapper>
        {!isDesktop && (
          <MobileCaption>
            <FormattedMessage
              id="site_pdp.wear_it_with_heading"
              defaultMessage="Wear It With"
            />
          </MobileCaption>
        )}
        <ProductsWrapper>
          {isDesktop ? (
            <UpsellCarousel
              products={products}
              psrc={productSource}
              isPlusSizeSelected={isPlusSizeSelected}
              {...wearItWithCarouselConfig}
            />
          ) : (
            <UpsellList
              products={products.slice(0, 6)}
              psrc={productSource}
              isPlusSizeSelected={isPlusSizeSelected}
            />
          )}
        </ProductsWrapper>
      </WearItWithSection>
      <Separator thickness={2} separatorStyle={separatorStyle} />
    </>
  );
};

RecommendedWearItWith.propTypes = {
  product: PropTypes.object,
};

export default RecommendedWearItWith;
